import { useEffect, useState } from "react";
import { ArrowLeft, ArrowRight, BookImage, Expand, ImagePlus, LoaderCircle, LockKeyhole, Trash2 } from "lucide-react";

export default function PhotoStage({ album, onUnlock, emptyAction, curation }) {
  const [index, setIndex] = useState(0);
  const [full, setFull] = useState(false);
  const photos = album?.photos || [];

  useEffect(() => setIndex(0), [album?.id]);
  useEffect(() => setIndex((value) => value >= photos.length ? 0 : value), [photos.length]);
  useEffect(() => {
    function keyboard(event) {
      if (!photos.length) return;
      if (event.key === "ArrowLeft") setIndex((value) => (value - 1 + photos.length) % photos.length);
      if (event.key === "ArrowRight") setIndex((value) => (value + 1) % photos.length);
      if (event.key === "Escape") setFull(false);
    }
    window.addEventListener("keydown", keyboard);
    return () => window.removeEventListener("keydown", keyboard);
  }, [photos.length]);

  if (album?.locked) {
    return (
      <div className="photo-stage photo-stage--empty">
        <LockKeyhole aria-hidden="true" />
        <h3>Family album</h3>
        <p>This album is visible after member sign in.</p>
        <button className="text-link" type="button" onClick={onUnlock}>Unlock album</button>
      </div>
    );
  }

  if (!photos.length) {
    return (
      <div className="photo-stage photo-stage--empty">
        <div className="photo-stage__empty-visual" aria-hidden="true">
          <span /><span /><span />
          <ImagePlus />
          <b>ADD<br />THE<br />MOMENT</b>
        </div>
        <div className="photo-stage__empty-copy">
          <p className="kicker">Archive in progress</p>
          <h3>Bring this chapter into focus.</h3>
          <p>The story is here. Add the photographs that belong with it and give the class a shared album instead of another disappearing chat thread.</p>
          {emptyAction}
        </div>
      </div>
    );
  }

  const visibleIndex = index >= photos.length ? 0 : index;
  const photo = photos[visibleIndex];
  const move = (direction) => setIndex((value) => (value + direction + photos.length) % photos.length);
  const moving = curation?.movingPhotoId === photo.id;
  const deleting = curation?.deletingPhotoId === photo.id;

  return (
    <div className={full ? "photo-stage is-full" : "photo-stage"}>
      <div className="photo-stage__frame">
        <img src={photo.src} alt={photo.alt} />
        <div className="photo-stage__wash" />
        <button className="icon-button photo-stage__expand" type="button" onClick={() => setFull((value) => !value)} aria-label={full ? "Close full screen photograph" : "View photograph full screen"}>
          <Expand aria-hidden="true" />
        </button>
        {curation && !full && (
          <div className="photo-stage__actions">
            <button className="photo-stage__curate" type="button" onClick={() => curation.move(photo)} disabled={curation.busy}>
              {moving ? <LoaderCircle className="is-spinning" aria-hidden="true" /> : <BookImage aria-hidden="true" />}
              {moving ? "Moving to College Days…" : "Add to College Days"}
            </button>
            <button className="photo-stage__delete" type="button" onClick={() => curation.delete(photo)} disabled={curation.busy}>
              {deleting ? <LoaderCircle className="is-spinning" aria-hidden="true" /> : <Trash2 aria-hidden="true" />}
              {deleting ? "Deleting…" : "Delete photo"}
            </button>
          </div>
        )}
        <button className="photo-stage__arrow is-left" type="button" onClick={() => move(-1)} aria-label="Previous photograph">
          <ArrowLeft aria-hidden="true" />
        </button>
        <button className="photo-stage__arrow is-right" type="button" onClick={() => move(1)} aria-label="Next photograph">
          <ArrowRight aria-hidden="true" />
        </button>
        <div className="photo-stage__caption">
          <span>{String(visibleIndex + 1).padStart(2, "0")} / {String(photos.length).padStart(2, "0")}</span>
          <p>{photo.alt}</p>
        </div>
      </div>
      <input
        className="photo-stage__scrubber"
        type="range"
        min="0"
        max={photos.length - 1}
        value={visibleIndex}
        onChange={(event) => setIndex(Number(event.target.value))}
        aria-label={`Photograph ${visibleIndex + 1} of ${photos.length}`}
      />
      <div className="photo-stage__progress" aria-hidden="true">
        <span style={{ width: `${((visibleIndex + 1) / photos.length) * 100}%` }} />
      </div>
    </div>
  );
}
